import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { card, h1, h2, sub, btn, RGB, Icon, ICONS, errMsg } from '../ext/extUi';
import MatchSeatingEditor, { seatingComplete } from '../ext/stage/MatchSeatingEditor';

// "Stage seating" of one panel match — the same venue screen as the external tournaments' match
// seating: which team sits on which side and the 5 seated players of each team. Only this match
// changes; the teams' default seating stays as it is. Display boards get the change right away
// (seating_update on the v1 streams). Route: /admin/matches/:id/seating

export default function MatchSeatingPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [value, setValue] = useState(null);
  const [savedValue, setSavedValue] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const apply = (d) => { setData(d); setValue(d.value); setSavedValue(d.saved ? d.value : null); };

  useEffect(() => {
    axios.get(`/api/v1/matches/${id}/seating/editor`).then(r => apply(r.data))
      .catch(e => setMsg({ error: true, text: errMsg(e, t('ext.loadFailed', 'Failed to load')) }));
  }, [id, t]);

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      await axios.put(`/api/v1/matches/${id}/seating`, value);
      const r = await axios.get(`/api/v1/matches/${id}/seating/editor`);
      apply(r.data);
      setMsg({ text: t('extSeating.matchSaved', 'Seating saved for this match') });
    } catch (e) { setMsg({ error: true, text: errMsg(e, t('ext.saveFailed', 'Could not save')) }); }
    finally { setBusy(false); }
  };

  if (!data) return <div style={sub}>{msg ? msg.text : t('common.loading', 'Loading...')}</div>;
  const short = ['team1', 'team2'].filter(k => data.teams[k].players.length < 5);
  const ok = !short.length && seatingComplete(value);
  const dirty = JSON.stringify(value) !== JSON.stringify(savedValue);

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '1.25rem', paddingBottom: '0.75rem' }}>
        <Link to={`/admin/matches/${id}`} style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}><Icon d={ICONS.back} /> {t('extSeating.backToMatch', 'Back to the match')}</Link>
        <h1 style={{ ...h1, marginTop: '0.35rem' }}>{t('extSeating.matchTitle', 'Stage seating · {{a}} vs {{b}}', { a: data.teams.team1.name, b: data.teams.team2.name })}</h1>
      </div>
      <div style={card}>
        <h2 style={h2}>{t('extSeating.matchSeating', 'Seating for this match')}</h2>
        <p style={sub}>{t('extSeating.matchSeatingHint', 'Seen from the crowd. Changes apply to this match only; the teams’ default seating is not changed. Display boards get the update on the API streams right away.')}</p>
        {short.length > 0
          ? short.map(k => <p key={k} style={{ ...sub, color: 'var(--accent-amber)' }}>{t('seating.matchNeedsFive', '{{team}} has {{count}} player(s) in this match. Stage seating needs 5.', { team: data.teams[k].name, count: data.teams[k].players.length })}</p>)
          : (
            <>
              {!data.saved && <p style={{ ...sub, color: 'var(--accent-amber)' }}>{t('seating.matchNotSaved', 'No seating saved for this match yet: this is the teams’ default seating. Save it to send it to the display boards.')}</p>}
              <MatchSeatingEditor teams={data.teams} value={value} onChange={setValue} />
            </>
          )}
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginTop: '0.9rem', flexWrap: 'wrap' }}>
          {msg && <span style={{ fontSize: '0.8rem', color: msg.error ? 'var(--accent-red)' : 'var(--accent-green)' }}>{msg.text}</span>}
          <span style={{ flex: 1 }} />
          {savedValue && dirty && <button type="button" style={btn(RGB.gray)} onClick={() => setValue(savedValue)}>{t('extSeating.discard', 'Discard changes')}</button>}
          {!short.length && <button type="button" disabled={busy || !ok || !dirty} style={{ ...btn(RGB.green, true), opacity: ok && dirty ? 1 : 0.5 }} onClick={save}>{busy ? t('common.loading', 'Loading...') : t('extSeating.saveSeating', 'Save seating')}</button>}
        </div>
      </div>
    </div>
  );
}
